import {
  approvalInputDigest,
  currentSubmissionContext,
  redactJson,
  sameApprovalOperation,
} from "@fabric-harness/sdk";
import type {
  ActorIdentity,
  ApprovalPolicyRule,
  CapabilityPolicy,
  FabricPrincipal,
  JsonObject,
  JsonValue,
  ToolDef,
} from "@fabric-harness/sdk";

/**
 * Governance for Databricks tools is **layered on top of Unity Catalog, never a replacement.** UC
 * enforces table/row/column ACLs natively via the acting principal (see identity.ts). These helpers
 * add the things UC doesn't: an **audit/lineage** stamp on every tool call, **approval routing** for
 * sensitive operations, and an **egress allowlist** pinned to the workspace — all expressed through
 * the SDK's existing `CapabilityPolicy`, so the loop enforces them without a bespoke engine.
 */

export interface DatabricksLineageRecord {
  tool: string;
  /** From the tool's `metadata.service` (e.g. 'sql', 'unity-catalog', 'jobs'). */
  service?: string;
  provider?: string;
  /** Stable label for the acting principal — never the token. */
  principal?: string;
  /** Durable submission owning this tool call (v2 correlation, from the ambient submission context). */
  submissionId?: string;
  attemptId?: string;
  tenantId?: string;
  catalog?: string;
  schema?: string;
  /** Governed object identifier (table, index, pipeline, job, endpoint, or statement target). */
  object?: string;
  /** Every structured resource declared by the tool. */
  resources?: DatabricksGovernedResource[];
  /** Approval identities are distinct from the principal that executed the call. */
  approvers?: ActorIdentity[];
  /** Tool effect copied from metadata (`read`, `write`, or `execute`). */
  operation?: string;
  /** Stable MCP/session tool-call identity. */
  toolCallId?: string;
  /** Canonical digest binding this record to the exact input. */
  inputDigest?: string;
  /** Effective policy version resolved at the execution boundary. */
  policyVersion?: string;
  input: JsonObject;
  startedAt: string;
  finishedAt: string;
  ok: boolean;
  error?: string;
}

export interface DatabricksGovernanceOptions {
  /** Stable label for the acting principal, recorded in lineage. Never pass a token here. */
  principal?: string;
  /** Sink for lineage/audit records. Defaults to no-op; wire to OTel / `onEvent` at the app layer. */
  onLineage?: (record: DatabricksLineageRecord) => void;
  /**
   * Optional belt-and-suspenders catalog allowlist. Calls whose input references a catalog outside
   * the list are rejected before execution. UC remains the source of truth; this is defense in depth.
   */
  catalogs?: string[];
  /** Verified principal bound to a request-scoped bundle. */
  executingPrincipal?: FabricPrincipal;
  /** Services whose model-exposed calls must carry a valid ApprovalGrant. */
  requireApprovalGrantFor?: string[];
  /** Exact authoring tools whose calls must carry a valid ApprovalGrant. */
  requireApprovalGrantForTools?: string[];
  /** Clock injection for tests. */
  now?: () => Date;
}

export interface DatabricksGovernanceResourceDescriptor {
  kind: string;
  /** Extended JSON Pointer over public camelCase input; `*` matches every array item. */
  path?: string;
  /** Static governed resource bound by a trusted adapter rather than model-supplied input. */
  value?: string;
  catalogQualified?: boolean;
  optional?: boolean;
}

export interface DatabricksGovernanceMetadata {
  resources: DatabricksGovernanceResourceDescriptor[];
  secretFields?: string[];
}

export interface DatabricksGovernedResource {
  kind: string;
  value: string;
  path: string;
  catalog?: string;
}

function metaString(tool: ToolDef, key: string): string | undefined {
  const value = (tool.metadata as Record<string, unknown> | undefined)?.[key];
  return typeof value === "string" ? value : undefined;
}

function inputString(input: unknown, key: string): string | undefined {
  if (input && typeof input === "object" && key in input) {
    const value = (input as Record<string, unknown>)[key];
    if (typeof value === "string") return value;
  }
  return undefined;
}

function governedObject(input: unknown): string | undefined {
  for (const key of [
    "fullName",
    "table",
    "index",
    "pipelineId",
    "jobId",
    "endpoint",
    "notebookPath",
  ]) {
    const value = inputString(input, key);
    if (value) return value;
  }
  const catalog = inputString(input, "catalog");
  const schema = inputString(input, "schema");
  return catalog ? [catalog, schema].filter(Boolean).join(".") : undefined;
}

function catalogFromInput(input: unknown): string | undefined {
  const explicit = inputString(input, "catalog");
  if (explicit) return explicit;
  const object = governedObject(input);
  return object?.includes(".") ? object.split(".")[0] : undefined;
}

export function governanceMetadata(tool: ToolDef): DatabricksGovernanceMetadata | undefined {
  const value = (tool.metadata as Record<string, unknown> | undefined)?.governance;
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const resources = (value as { resources?: unknown }).resources;
  if (!Array.isArray(resources)) return undefined;
  return {
    resources: resources.map((resource, index) =>
      parseResourceDescriptor(resource, tool.name, index),
    ),
    ...((value as { secretFields?: unknown }).secretFields !== undefined
      ? {
          secretFields: parseStringArray(
            (value as { secretFields?: unknown }).secretFields,
            `${tool.name} governance.secretFields`,
          ),
        }
      : {}),
  };
}

/** Validate descriptor syntax and that each path can be traversed in the declared input schema. */
export function validateDatabricksGovernanceDescriptor(tool: ToolDef, required = false): void {
  const metadata = governanceMetadata(tool);
  if (!metadata) {
    if (required)
      throw new Error(
        `databricks governance: authoring tool "${tool.name}" must declare metadata.governance.resources.`,
      );
    return;
  }
  if (metadata.resources.length === 0)
    throw new Error(`databricks governance: tool "${tool.name}" declares no governed resources.`);
  const hasInputResources = metadata.resources.some((descriptor) => descriptor.path !== undefined);
  if (hasInputResources && (!tool.inputSchema || typeof tool.inputSchema !== "object"))
    throw new Error(
      `databricks governance: tool "${tool.name}" needs an inputSchema for resource validation.`,
    );
  for (const descriptor of metadata.resources)
    if (descriptor.path)
      validateSchemaPath(tool.name, tool.inputSchema as Record<string, unknown>, descriptor);
}

export function extractDatabricksGovernedResources(
  tool: ToolDef,
  input: unknown,
): DatabricksGovernedResource[] {
  const metadata = governanceMetadata(tool);
  if (!metadata) return legacyResources(input);
  const resources: DatabricksGovernedResource[] = [];
  for (const descriptor of metadata.resources) {
    if (descriptor.value !== undefined) {
      resources.push({
        kind: descriptor.kind,
        value: descriptor.value,
        path: "metadata",
        ...(descriptor.catalogQualified &&
        (descriptor.kind === "catalog" || descriptor.value.includes("."))
          ? {
              catalog:
                descriptor.kind === "catalog" ? descriptor.value : descriptor.value.split(".")[0],
            }
          : {}),
      });
      continue;
    }
    if (!descriptor.path) continue;
    const values = pointerValues(input, descriptor.path);
    if (values.length === 0 && !descriptor.optional) {
      throw new Error(
        `databricks governance: required resource path "${descriptor.path}" matched no values for tool "${tool.name}".`,
      );
    }
    for (const value of values) {
      if (typeof value !== "string" && typeof value !== "number") {
        throw new Error(
          `databricks governance: resource path "${descriptor.path}" on tool "${tool.name}" must resolve to strings or numbers.`,
        );
      }
      const text = String(value);
      if (!text)
        throw new Error(
          `databricks governance: resource path "${descriptor.path}" on tool "${tool.name}" resolved to an empty value.`,
        );
      resources.push({
        kind: descriptor.kind,
        value: text,
        path: descriptor.path,
        ...(descriptor.catalogQualified && (descriptor.kind === "catalog" || text.includes("."))
          ? { catalog: descriptor.kind === "catalog" ? text : text.split(".")[0] }
          : {}),
      });
    }
  }
  return resources;
}

/** Wraps one Databricks tool to stamp lineage/audit and enforce the optional catalog allowlist. */
export function withGovernance<I, O>(
  tool: ToolDef<I, O>,
  options: DatabricksGovernanceOptions = {},
): ToolDef<I, O> {
  const inner = tool.execute;
  if (!inner) return tool;
  validateDatabricksGovernanceDescriptor(tool);
  const now = options.now ?? (() => new Date());

  return {
    ...tool,
    metadata: { ...(tool.metadata ?? {}), governed: true },
    async execute(input: I, context) {
      const resources = extractDatabricksGovernedResources(tool, input);
      const catalogs = [
        ...new Set(resources.flatMap((resource) => (resource.catalog ? [resource.catalog] : []))),
      ];
      const catalog = catalogs[0] ?? catalogFromInput(input);
      const schema = inputString(input, "schema");
      const object = governedObject(input);
      const startedAt = now().toISOString();
      const service = metaString(tool, "service");
      const provider = metaString(tool, "provider");
      const operation = metaString(tool, "effect");
      // Correlate with the durable submission owning this turn (set by the
      // submission runner); the acting principal falls back to the actor's
      // governed identity when no explicit label was configured.
      const submission = currentSubmissionContext();
      const executingPrincipal = context?.actor?.principal ?? options.executingPrincipal;
      const principal =
        context?.actor?.principal?.ucPrincipal ??
        context?.actor?.principal?.id ??
        options.principal ??
        options.executingPrincipal?.ucPrincipal ??
        options.executingPrincipal?.id ??
        submission?.actor?.principal?.id;
      const base = {
        tool: tool.name,
        ...(service !== undefined ? { service } : {}),
        ...(provider !== undefined ? { provider } : {}),
        ...(principal !== undefined ? { principal } : {}),
        ...(submission
          ? {
              submissionId: submission.submissionId,
              attemptId: submission.attemptId,
              ...(submission.tenantId !== undefined ? { tenantId: submission.tenantId } : {}),
            }
          : {}),
        ...(!submission && context?.tenantId !== undefined ? { tenantId: context.tenantId } : {}),
        ...(catalog !== undefined ? { catalog } : {}),
        ...(schema !== undefined ? { schema } : {}),
        ...(object !== undefined ? { object } : {}),
        ...(resources.length > 0 ? { resources } : {}),
        ...(context?.approval?.approvers.length ? { approvers: context.approval.approvers } : {}),
        ...(operation !== undefined ? { operation } : {}),
        ...(context?.toolCallId ? { toolCallId: context.toolCallId } : {}),
        ...(context?.inputDigest
          ? { inputDigest: context.inputDigest }
          : { inputDigest: approvalInputDigest(input) }),
        ...(context?.policyVersion ? { policyVersion: context.policyVersion } : {}),
        // Redact secret-ish keys before the input enters the audit/lineage record.
        input: redactToolInput(input, governanceMetadata(tool)?.secretFields),
        startedAt,
      };
      const deniedCatalog = options.catalogs
        ? catalogs.find((candidate) => !options.catalogs?.includes(candidate))
        : undefined;
      if (options.catalogs && deniedCatalog) {
        const error = `databricks governance: catalog "${deniedCatalog}" is not in the allowed list (${options.catalogs.join(", ")}).`;
        options.onLineage?.({ ...base, finishedAt: now().toISOString(), ok: false, error });
        throw new Error(error);
      }
      try {
        if (
          service &&
          (operation === "write" || operation === "execute") &&
          (options.requireApprovalGrantFor?.includes(service) ||
            options.requireApprovalGrantForTools?.includes(tool.name))
        ) {
          if (!executingPrincipal)
            throw new Error(
              `databricks governance: service "${service}" requires a verified executing principal.`,
            );
          if (
            !context?.approval ||
            !context.toolCallId ||
            !sameApprovalOperation(context.approval, {
              toolCallId: context.toolCallId,
              toolInput: input,
              principal: executingPrincipal,
            })
          ) {
            throw new Error(
              `databricks governance: tool "${tool.name}" requires an ApprovalGrant bound to this call, input, and executing principal.`,
            );
          }
        }
        const result = await inner(input, context);
        options.onLineage?.({ ...base, finishedAt: now().toISOString(), ok: true });
        return result;
      } catch (error) {
        options.onLineage?.({
          ...base,
          finishedAt: now().toISOString(),
          ok: false,
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
  };
}

function parseResourceDescriptor(
  value: unknown,
  toolName: string,
  index: number,
): DatabricksGovernanceResourceDescriptor {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error(
      `databricks governance: invalid resource descriptor ${index} on tool "${toolName}".`,
    );
  const row = value as Record<string, unknown>;
  const path = typeof row.path === "string" ? row.path : undefined;
  const staticValue = typeof row.value === "string" ? row.value : undefined;
  if (
    typeof row.kind !== "string" ||
    !row.kind ||
    (path === undefined && staticValue === undefined) ||
    (path !== undefined && staticValue !== undefined) ||
    (path !== undefined && !path.startsWith("/")) ||
    staticValue === ""
  ) {
    throw new Error(
      `databricks governance: descriptor ${index} on tool "${toolName}" requires kind and exactly one absolute path or static value.`,
    );
  }
  return {
    kind: row.kind,
    ...(path !== undefined ? { path } : { value: staticValue as string }),
    ...(row.catalogQualified === true ? { catalogQualified: true } : {}),
    ...(row.optional === true ? { optional: true } : {}),
  };
}

function parseStringArray(value: unknown, label: string): string[] {
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string"))
    throw new Error(`databricks governance: ${label} must be a string array.`);
  return value as string[];
}

function pointerSegments(path: string): string[] {
  return path
    .slice(1)
    .split("/")
    .map((segment) => segment.replace(/~1/g, "/").replace(/~0/g, "~"));
}

function pointerValues(input: unknown, path: string): unknown[] {
  let current: unknown[] = [input];
  for (const segment of pointerSegments(path)) {
    current = current
      .flatMap((value) => {
        if (segment === "*") return Array.isArray(value) ? value : [];
        if (!value || typeof value !== "object" || Array.isArray(value)) return [];
        return segment in value ? [(value as Record<string, unknown>)[segment]] : [];
      })
      .filter((value) => value !== undefined && value !== null);
  }
  return current;
}

function validateSchemaPath(
  toolName: string,
  schema: Record<string, unknown>,
  descriptor: DatabricksGovernanceResourceDescriptor,
): void {
  const path = descriptor.path;
  if (path === undefined) return;
  let current: Record<string, unknown>[] = [schema];
  for (const segment of pointerSegments(path)) {
    const variants = current.flatMap(schemaVariants);
    const next: Record<string, unknown>[] = [];
    let missing = 0;
    for (const variant of variants) {
      if (segment === "*") {
        const items = variant.items;
        if (variant.type === "array" && items && typeof items === "object" && !Array.isArray(items))
          next.push(items as Record<string, unknown>);
        else missing += 1;
        continue;
      }
      const properties = variant.properties;
      const child =
        properties && typeof properties === "object" && !Array.isArray(properties)
          ? (properties as Record<string, unknown>)[segment]
          : undefined;
      if (child && typeof child === "object" && !Array.isArray(child))
        next.push(child as Record<string, unknown>);
      else missing += 1;
    }
    if (next.length === 0)
      throw new Error(
        `databricks governance: path "${descriptor.path}" does not exist in inputSchema for tool "${toolName}".`,
      );
    if (!descriptor.optional && missing > 0)
      throw new Error(
        `databricks governance: required path "${descriptor.path}" is not present in every inputSchema branch for tool "${toolName}". Mark the descriptor optional or make the schema branch mandatory.`,
      );
    current = next;
  }
}

function schemaVariants(schema: Record<string, unknown>): Record<string, unknown>[] {
  const variants = Array.isArray(schema.oneOf)
    ? schema.oneOf
    : Array.isArray(schema.anyOf)
      ? schema.anyOf
      : undefined;
  if (!variants) return [schema];
  return variants.filter(
    (value): value is Record<string, unknown> =>
      Boolean(value) && typeof value === "object" && !Array.isArray(value),
  );
}

function legacyResources(input: unknown): DatabricksGovernedResource[] {
  const value = governedObject(input);
  if (!value) return [];
  const catalog = catalogFromInput(input);
  return [{ kind: "object", value, path: "legacy-heuristic", ...(catalog ? { catalog } : {}) }];
}

function redactToolInput(input: unknown, secretFields: string[] | undefined): JsonObject {
  const clone = redactNamedFields((input ?? {}) as JsonValue, new Set(secretFields ?? []));
  return redactJson(clone as JsonObject);
}

function redactNamedFields(value: JsonValue, names: Set<string>): JsonValue {
  if (Array.isArray(value)) return value.map((item) => redactNamedFields(item, names));
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      names.has(key) ? "[REDACTED]" : redactNamedFields(child, names),
    ]),
  );
}

/** Wraps a set of Databricks tools with {@link withGovernance}. */
export function withGovernanceTools(
  tools: ToolDef[],
  options: DatabricksGovernanceOptions = {},
): ToolDef[] {
  return tools.map((tool) => withGovernance(tool, options));
}

export interface DatabricksApprovalRuleOptions {
  /** Opaque approval audience (e.g. 'data-steward'). */
  audience: string;
  /** Restrict to these `metadata.service` values. Default: gate every write/execute tool. */
  services?: string[];
  reason?: string;
  ttlSeconds?: number;
}

function shouldGate(tool: ToolDef, services?: string[]): boolean {
  const effect = (tool.metadata as Record<string, unknown> | undefined)?.effect;
  if (effect !== "write" && effect !== "execute") return false;
  return !services || services.includes(metaString(tool, "service") ?? "");
}

/** Builds approval rules (one per gated tool) for `CapabilityPolicy.toolPolicy.approvalRules`. */
export function databricksApprovalRules(
  tools: ToolDef[],
  options: DatabricksApprovalRuleOptions,
): ApprovalPolicyRule[] {
  return tools
    .filter((tool) => shouldGate(tool, options.services))
    .map((tool) => ({
      pattern: tool.name,
      audience: options.audience,
      // ${name} is templated by the policy layer at request time — keep it literal here.
      reason: options.reason ?? "Databricks ${name} requires data-steward approval.",
      ...(options.ttlSeconds !== undefined ? { ttlSeconds: options.ttlSeconds } : {}),
    }));
}

export interface DatabricksGovernancePolicyOptions {
  tools: ToolDef[];
  /** Workspace host(s) to pin outbound egress to (URL or bare host). */
  host?: string | string[];
  /** When set, sensitive tools route to this approval audience. */
  stewardAudience?: string;
  approvalServices?: string[];
  ttlSeconds?: number;
}

export interface AnalyticsCopilotGovernanceOptions {
  /** Approval audience for arbitrary SQL execution and Genie lifecycle management. */
  stewardAudience: string;
  /** Belt-and-suspenders catalog allowlist enforced before tool execution. */
  catalogs?: string[];
  approvalTtlSeconds?: number;
  /** Stable label recorded in lineage. Never pass a token. */
  principalLabel?: string;
  onLineage?: (record: DatabricksLineageRecord) => void;
}

export interface AnalyticsCopilotGovernance {
  stewardAudience: string;
  /** SQL and Genie are the only authoring services enabled by this focused policy pack. */
  approvalServices: string[];
  catalogs?: string[];
  approvalTtlSeconds?: number;
  principalLabel?: string;
  onLineage?: (record: DatabricksLineageRecord) => void;
}

/**
 * Governance defaults for the analytics-copilot path. Safe Genie questions and `sql_read` carry a
 * read effect and remain interactive; arbitrary SQL execution and Genie lifecycle tools retain
 * steward approval. Enabling another authoring service with this pack fails bundle initialization
 * until the caller deliberately expands its approval scope.
 */
export function analyticsCopilotGovernance(
  options: AnalyticsCopilotGovernanceOptions,
): AnalyticsCopilotGovernance {
  const stewardAudience = options.stewardAudience.trim();
  if (!stewardAudience)
    throw new Error("databricks analytics copilot governance requires a steward audience.");
  return {
    stewardAudience,
    approvalServices: ["sql", "genie"],
    ...(options.catalogs ? { catalogs: [...options.catalogs] } : {}),
    ...(options.approvalTtlSeconds !== undefined
      ? { approvalTtlSeconds: options.approvalTtlSeconds }
      : {}),
    ...(options.principalLabel ? { principalLabel: options.principalLabel } : {}),
    ...(options.onLineage ? { onLineage: options.onLineage } : {}),
  };
}

function toHostPatterns(host: string | string[] | undefined): string[] {
  if (!host) return [];
  const list = Array.isArray(host) ? host : [host];
  return list.map((entry) => {
    try {
      return new URL(entry.includes("://") ? entry : `https://${entry}`).host;
    } catch {
      return entry;
    }
  });
}

/**
 * Assembles a `CapabilityPolicy` for a Databricks tool set: approval routing for sensitive
 * operations (if `stewardAudience` is set) plus an egress allowlist pinned to the workspace host(s).
 * Used by the `databricks()` bundle; can also be merged into an agent's own policy.
 */
export function databricksGovernancePolicy(
  options: DatabricksGovernancePolicyOptions,
): CapabilityPolicy {
  const policy: CapabilityPolicy = {};
  if (options.stewardAudience) {
    policy.toolPolicy = {
      approvalRules: databricksApprovalRules(options.tools, {
        audience: options.stewardAudience,
        ...(options.approvalServices ? { services: options.approvalServices } : {}),
        ...(options.ttlSeconds !== undefined ? { ttlSeconds: options.ttlSeconds } : {}),
      }),
    };
  }
  const hosts = toHostPatterns(options.host);
  if (hosts.length > 0) policy.network = { mode: "allowlist", hosts };
  return policy;
}
